import React from 'react';
import {
  BOARD_EDITORIAL_LIMITS,
  type AdminBoardEditorialDto,
  type AdminBoardWorkDto,
} from '@living-library/contracts';
import type { BoardRequest } from './board-api';
import { button, c, card, emptyText, LanguageEditor } from './board-ui';
import { WorkPicker } from './work-picker';

export function newQuestion() {
  return {
    id: crypto.randomUUID(),
    prompt: emptyText(),
    options: Array.from({ length: 4 }, () => ({ id: crypto.randomUUID(), workId: '' })),
  };
}
export function BoardContentEditor({
  item,
  onChange,
  request,
  locked,
  onWork,
}: {
  item: AdminBoardEditorialDto;
  onChange(item: AdminBoardEditorialDto): void;
  request: BoardRequest;
  locked: boolean;
  onWork(work: AdminBoardWorkDto): void;
}) {
  const move = (index: number, by: number) => {
    const items = [...item.items];
    [items[index], items[index + by]] = [items[index + by], items[index]];
    onChange({ ...item, items });
  };
  if (item.kind === 'picks')
    return (
      <div className="grid gap-4">
        {item.items.map((pick, index) => (
          <article className={card} key={pick.id}>
            <div className="mb-3 flex flex-wrap justify-between gap-2">
              <span className="grid size-10 place-items-center rounded-full bg-thread-soft font-serif text-xl text-thread">
                {index + 1}
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={button}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  {c.up}
                </button>
                <button
                  type="button"
                  className={button}
                  disabled={index === item.items.length - 1}
                  onClick={() => move(index, 1)}
                >
                  {c.down}
                </button>
                <button
                  type="button"
                  className="admin-button-danger"
                  onClick={() =>
                    onChange({ ...item, items: item.items.filter((p) => p.id !== pick.id) })
                  }
                >
                  {c.removeItem}
                </button>
              </div>
            </div>
            <WorkPicker
              request={request}
              workId={pick.workId}
              onResolved={onWork}
              onChange={(work) =>
                onChange({
                  ...item,
                  items: item.items.map((p) => (p.id === pick.id ? { ...p, workId: work.id } : p)),
                })
              }
            />
            <LanguageEditor
              label={c.reason}
              value={pick.reason}
              limit={BOARD_EDITORIAL_LIMITS.reason}
              onChange={(reason) =>
                onChange({
                  ...item,
                  items: item.items.map((p) => (p.id === pick.id ? { ...p, reason } : p)),
                })
              }
            />
          </article>
        ))}
        <button
          className={`${button} justify-self-start`}
          type="button"
          disabled={item.items.length >= 50}
          onClick={() =>
            onChange({
              ...item,
              items: [...item.items, { id: crypto.randomUUID(), workId: '', reason: emptyText() }],
            })
          }
        >
          {c.addPick}
        </button>
      </div>
    );
  return (
    <div className="grid gap-4">
      {locked && (
        <p className="rounded-xl bg-canvas p-4 text-sm leading-6 text-brass-pale">{c.locked}</p>
      )}
      {item.questions.map((question, index) => (
        <article key={question.id} className={card}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-serif text-2xl">
              {c.question} {index + 1}
            </h2>
            <button
              className="admin-button-danger"
              type="button"
              disabled={locked || item.questions.length <= 3}
              onClick={() =>
                onChange({ ...item, questions: item.questions.filter((q) => q.id !== question.id) })
              }
            >
              {c.removeItem}
            </button>
          </div>
          <LanguageEditor
            label={c.prompt}
            value={question.prompt}
            limit={BOARD_EDITORIAL_LIMITS.prompt}
            onChange={(prompt) =>
              onChange({
                ...item,
                questions: item.questions.map((q) => (q.id === question.id ? { ...q, prompt } : q)),
              })
            }
          />
          <div className="grid gap-4 lg:grid-cols-2">
            {Array.from({ length: 4 }, (_, optionIndex) => {
              const option = question.options[optionIndex];
              return (
                <WorkPicker
                  key={optionIndex}
                  label={`${c.chooseWork} ${optionIndex + 1}`}
                  request={request}
                  workId={option?.workId ?? ''}
                  disabled={locked}
                  onResolved={onWork}
                  onChange={(work) => {
                    const options = Array.from(
                      { length: 4 },
                      (_, i) => question.options[i] ?? { id: crypto.randomUUID(), workId: '' },
                    );
                    options[optionIndex] = { ...options[optionIndex], workId: work.id };
                    onChange({
                      ...item,
                      questions: item.questions.map((q) =>
                        q.id === question.id ? { ...q, options } : q,
                      ),
                    });
                  }}
                />
              );
            })}
          </div>
        </article>
      ))}
      <button
        className={`${button} justify-self-start`}
        type="button"
        disabled={locked || item.questions.length >= 4}
        onClick={() => onChange({ ...item, questions: [...item.questions, newQuestion()] })}
      >
        {c.addQuestion}
      </button>
    </div>
  );
}
